/**
 * Frozen Products Cart Handler v2.1
 * 
 * Optimized for Symmetry theme - uses native cart events.
 * Reads frozen state from DOM (set by Liquid) - NO API calls needed.
 * 
 * IMPORTANT: Always resets ALL inline styles because Symmetry's mergeNodes
 * only updates data-attributes, not element content/styles.
 * 
 * @version 2.1.0
 */

(function() {
  'use strict';

  const FrozenProductsHandler = {
    
    /**
     * Initialize the handler
     */
    init() {
      // Initial state update
      this.updateVisibility();
      
      // Listen to Symmetry's cart merge event (fires after cart HTML updates)
      document.addEventListener('on:cart:after-merge', () => {
        this.updateVisibility();
      });
      
      // Also listen to general cart change (backup)
      document.addEventListener('on:cart:change', () => {
        // Small delay to let DOM update
        setTimeout(() => this.updateVisibility(), 100);
      });
      
      // Listen to delivery mode changes (dispatched by DeliveryModeService)
      document.addEventListener('deliveryMode:changed', () => {
        this.updateVisibility();
      });
      
      // Also listen for tab clicks (backup for delivery mode)
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-delivery-tab]')) {
          setTimeout(() => this.updateVisibility(), 50);
        }
      });
    },

    /**
     * Check if cart has frozen products (reads from DOM, set by Liquid)
     * @returns {boolean}
     */
    hasFrozenProducts() {
      const bar = document.querySelector('.free-shipping-bar');
      if (bar && bar.dataset.hasFrozen === 'true') {
        return true;
      }
      
      // Fallback: check if frozen warning exists in DOM
      const warning = document.querySelector('[data-frozen-warning]');
      return warning !== null;
    },

    /**
     * Check if pickup mode is active
     * @returns {boolean}
     */
    isPickupMode() {
      if (window.MurrenMurkina?.DeliveryModeService) {
        return window.MurrenMurkina.DeliveryModeService.isPickupMode();
      }
      
      try {
        return localStorage.getItem('murrenmurkina_delivery_mode') === 'pickup';
      } catch (e) {
        return false;
      }
    },

    /**
     * Update all frozen-related UI elements based on current state
     */
    updateVisibility() {
      const hasFrozen = this.hasFrozenProducts();
      const isPickup = this.isPickupMode();
      const showFrozenElements = hasFrozen && !isPickup;
      
      this.updateFreeShippingBar(showFrozenElements, isPickup, hasFrozen);
      this.updateWarningBox(showFrozenElements);
    },

    /**
     * Update free shipping bar state
     * ALWAYS resets all styles to ensure correct state after mergeNodes
     * 
     * @param {boolean} showFrozen - Show frozen message
     * @param {boolean} isPickup - Is pickup mode active
     * @param {boolean} hasFrozen - Cart contains frozen products
     */
    updateFreeShippingBar(showFrozen, isPickup, hasFrozen) {
      const bar = document.querySelector('.free-shipping-bar');
      if (!bar) return;
      
      // Get fresh progress value from data attribute (updated by Liquid)
      const progress = parseFloat(bar.dataset.freeShippingProgress) || 0;
      
      // Get all elements
      const frozenMsg = bar.querySelector('.free-shipping-bar__frozen-message');
      const msgNo = bar.querySelector('.free-shipping-bar__message--no');
      const msgYes = bar.querySelector('.free-shipping-bar__message--yes');
      const progressBar = bar.querySelector('.free-shipping-bar__bar');
      
      // === PICKUP MODE: Hide entire bar ===
      if (isPickup) {
        bar.style.display = 'none';
        return;
      }
      
      // === ONLINE MODE: Show bar ===
      bar.style.display = '';
      
      if (showFrozen) {
        // === FROZEN STATE ===
        bar.classList.add('free-shipping-bar--frozen');
        
        if (frozenMsg) frozenMsg.style.display = 'flex';
        if (msgNo) msgNo.style.display = 'none';
        if (msgYes) msgYes.style.display = 'none';
        if (progressBar) progressBar.style.display = 'none';
        
      } else {
        // === NORMAL STATE ===
        bar.classList.remove('free-shipping-bar--frozen');
        
        if (frozenMsg) frozenMsg.style.display = 'none';
        
        // Show correct message based on progress
        if (msgNo) msgNo.style.display = progress < 1 ? '' : 'none';
        if (msgYes) msgYes.style.display = progress >= 1 ? '' : 'none';
        if (progressBar) progressBar.style.display = '';
      }
    },

    /**
     * Update frozen warning box visibility
     * @param {boolean} show - Show the warning
     */
    updateWarningBox(show) {
      const warning = document.querySelector('[data-frozen-warning]');
      if (warning) {
        warning.style.display = show ? '' : 'none';
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FrozenProductsHandler.init());
  } else {
    FrozenProductsHandler.init();
  }

  // Expose globally
  window.FrozenProductsHandler = FrozenProductsHandler;

})();